'use client';
import { FC } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { ImageLoader } from '@/lib/imageLoader';
import { youtubeFormatter } from '@/lib/youtubeFormatter';
import s from './GalleryModal.module.css';
import scrollToId from '@/hooks/use-scroll-to-id';
import 'swiper/css';
import { UploadFileCollection } from '@/types/model';
import useIsMobile from '@/hooks/use-is-mobile';
import { ReactSVG } from 'react-svg';
import { hideDecorativeSvg } from '@/lib/a11y';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';

interface VideoItem {
  url: string;
}

interface GalleryModalProps {
  id: string;
  onClose: () => void;
  images: UploadFileCollection;
  videos: VideoItem[];
  showImages: string;
  showVideos: string;
}

const GalleryModal: FC<GalleryModalProps> = ({
  id,
  onClose,
  images,
  videos,
  showImages,
  showVideos
}) => {
  const isMobile = useIsMobile();
  const t = useTranslations('gallery');
  const doScroll = () => scrollToId(id);
  const total = images?.length ?? 0;
  const slidesToShow = isMobile ? 1 : Math.min(3, total);

  return (
    <AnimatePresence mode="wait">
      {(showImages === id || showVideos === id) && (
        <motion.div className={s.modal} key={showImages}>
          <button className={s.modal__closeBtn} onClick={onClose} aria-label={t('closeGallery')}>
            <ReactSVG
              className={s.logo_close}
              src="/icons/close.svg"
              wrapper="span"
              beforeInjection={(svg: SVGSVGElement) => {
                hideDecorativeSvg(svg);
                svg.classList.add('svg__icon__left');
              }}
            />
          </button>
          <motion.div
            className={s.viewer}
            key={id}
            id={`item-${id}`}
            onAnimationComplete={doScroll}
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { duration: 0.8 } }}
            exit={{ y: -80, opacity: 0 }}>
            <Swiper
              className={s.viewer__swiper}
              key={`swiper-${total}-${isMobile}`}
              slidesPerView={isMobile ? 1 : 3}
              centeredSlides
              spaceBetween={isMobile ? 0 : 17}
              loop={total > slidesToShow}>
              {showImages === id &&
                images?.map((image, index) => {
                  return (
                    <SwiperSlide className={s.viewer__swiper__slide} key={`image-${index + 1}`}>
                      {image?.url && (
                        <Image
                          src={ImageLoader({ src: image.url })}
                          alt={t('representativeImage')}
                          width={isMobile ? 340 : 1010}
                          height={isMobile ? 265 : 644}
                          sizes={isMobile ? '340px' : '1010px'}
                          quality={65}
                        />
                      )}
                    </SwiperSlide>
                  );
                })}

              {showVideos &&
                videos.map((video, index) => (
                  <SwiperSlide className={s.viewer__swiper__video} key={`video-${index}`}>
                    <iframe
                      title={`Video ${index + 1}`}
                      width={isMobile ? '340' : '1010'}
                      height={isMobile ? '265' : '644'}
                      src={youtubeFormatter(video.url)}
                      frameBorder="0"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className={s.media}
                    />
                  </SwiperSlide>
                ))}

              <SlideButtons showImages={showImages} showVideos={showVideos} id={id} />
            </Swiper>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function SlideButtons({
  showImages,
  showVideos,
  id
}: {
  showImages: string;
  showVideos: string;
  id: string;
}) {
  const swiper = useSwiper();
  const t = useTranslations('gallery');

  return (
    <div className={s.swiper_bottom}>
      {(showImages === id || showVideos === id) && (
        <div className={s.swiper_bottom__section}>
          <ReactSVG
            className={s.logo}
            src={showImages === id ? '/icons/boton-fotos.svg' : '/icons/boton-video.svg'}
            wrapper="span"
            beforeInjection={(svg: SVGSVGElement) => {
              hideDecorativeSvg(svg);
              svg.classList.add('svg__icon__left');
            }}
          />
          <p>{showImages === id ? t('photos') : t('videos')}</p>
        </div>
      )}

      <div className={s.swiper_buttons}>
        <button
          className={s.swiper_buttons__prev}
          onClick={() => swiper.slidePrev()}
          aria-label={t('prevItem')}>
          <ReactSVG src="/icons/flecha-boton.svg" beforeInjection={hideDecorativeSvg} />
        </button>
        <button onClick={() => swiper.slideNext()} aria-label={t('nextItem')}>
          <ReactSVG src="/icons/flecha-boton.svg" beforeInjection={hideDecorativeSvg} />
        </button>
      </div>
    </div>
  );
}

export default GalleryModal;
